import { useState } from 'react'
import { Box, Flex, Grid, Spinner, Text, VStack, toaster, useT } from '@minetrace/ui'
import { AnimatePresence, motion } from 'framer-motion'
import {
  LuClock,
  LuFileCode2,
  LuKeyRound,
  LuPrinter,
  LuShieldCheck,
  LuTriangleAlert,
} from 'react-icons/lu'
import type {
  IndiceVerificacionResponse,
  IndiceVersionResponse,
} from '../../../api/models/response/indiceDigital'
import { expedienteService } from '../../../api/services'
import { brand, m3, themeColors } from '../../../theme'
import { formatDateTime } from '../../../lib/dates'
import { Chip, Field } from './ui'
import { downloadAuthenticated } from './helpers'
import { VerificacionPanel } from './VerificacionPanel'

const MotionBox = motion.create(Box)

const EMPTY_USER_ID = '00000000-0000-0000-0000-000000000000'

export function VersionesTab({
  code,
  versiones,
  verificaciones,
  onVerified,
}: {
  code: string
  versiones: IndiceVersionResponse[]
  verificaciones: Record<number, IndiceVerificacionResponse>
  onVerified: (v: IndiceVerificacionResponse) => void
}) {
  const { t } = useT()

  return (
    <VStack alignItems="stretch" gap={4}>
      <Box>
        <Text fontSize="sm" fontWeight="700" color={themeColors.text}>
          {t('expedientes.electronico.versiones.title')}
        </Text>
        <Text fontSize="xs" color={themeColors.textMuted} mt={1}>
          {t('expedientes.electronico.versiones.subtitle')}
        </Text>
      </Box>

      {versiones.length === 0 && (
        <Text fontSize="sm" color={themeColors.textMuted} py={8} textAlign="center">
          {t('expedientes.electronico.versiones.empty')}
        </Text>
      )}

      {versiones.map((v, idx) => (
        <VersionCard
          key={v.versionNumber}
          code={code}
          version={v}
          index={idx}
          verificacion={verificaciones[v.versionNumber] ?? null}
          onVerified={onVerified}
        />
      ))}
    </VStack>
  )
}

function VersionCard({
  code,
  version,
  index,
  verificacion,
  onVerified,
}: {
  code: string
  version: IndiceVersionResponse
  index: number
  verificacion: IndiceVerificacionResponse | null
  onVerified: (v: IndiceVerificacionResponse) => void
}) {
  const { t } = useT()
  const [verifying, setVerifying] = useState(false)

  const handleVerify = async () => {
    setVerifying(true)
    try {
      onVerified(await expedienteService.verifyIndiceVersion(code, version.versionNumber))
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'No se pudo comprobar la versión' })
    } finally {
      setVerifying(false)
    }
  }

  const handleDownload = async (kind: 'xml' | 'pdf') => {
    const url = kind === 'xml'
      ? expedienteService.getIndiceXmlUrl(code, version.versionNumber)
      : expedienteService.getIndicePdfUrl(code, version.versionNumber)
    const filename = `${code}_IndiceDigital_v${version.versionNumber}.${kind}`
    try {
      await downloadAuthenticated(url, filename)
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'No se pudo descargar' })
    }
  }

  return (
    <MotionBox
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: Math.min(index, 8) * 0.03 }}
      bg={themeColors.bgPanel}
      border="1px solid"
      borderColor={themeColors.border}
      borderRadius="xl"
      p={5}
    >
      <Flex justifyContent="space-between" alignItems="flex-start" gap={4} flexWrap="wrap" mb={4}>
        <Flex alignItems="center" gap={3}>
          <Flex
            w="40px"
            h="40px"
            borderRadius="lg"
            bg={`${brand.green}12`}
            color={brand.green}
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <LuShieldCheck size={19} />
          </Flex>
          <Box>
            <Text fontSize="md" fontWeight="700" color={themeColors.text}>
              {t('expedientes.electronico.versiones.version')} {version.versionNumber}
            </Text>
            <Text fontSize="xs" color={themeColors.textMuted}>
              {t('expedientes.electronico.versiones.sealedAt')} {formatDateTime(version.generatedAt)}
              {' · '}
              {version.generatedByUserId === EMPTY_USER_ID || !version.generatedByUserName
                ? t('expedientes.electronico.versiones.system')
                : version.generatedByUserName}
            </Text>
          </Box>
        </Flex>

        <Flex gap={2} flexWrap="wrap" alignItems="center">
          <Chip color={brand.green} bg={`${brand.green}12`} mono>
            {version.cvd}
          </Chip>
          {/* Las tres declaraciones de honestidad de la versión, en el propio bloque
              de la versión: la fecha no la pone una TSA, la clave puede ser la de
              desarrollo, y el XML sellado puede no estar. */}
          {!version.hasTimeStamp && (
            <Chip
              icon={<LuClock size={11} />}
              color={themeColors.textMuted}
              bg={themeColors.bgHover}
            >
              {t('expedientes.electronico.versiones.noTimestamp')}
            </Chip>
          )}
          {version.signingKeyIsEphemeral && (
            <Chip
              icon={<LuTriangleAlert size={11} />}
              color={m3.error}
              bg="rgba(186, 26, 26, 0.10)"
            >
              {t('expedientes.electronico.versiones.ephemeral')}
            </Chip>
          )}
        </Flex>
      </Flex>

      <Grid templateColumns="repeat(auto-fit, minmax(170px, 1fr))" gap={4} mb={4}>
        <Field
          label={t('expedientes.electronico.versiones.trigger')}
          value={version.triggerTypeName}
        />
        <Field
          label={t('expedientes.electronico.versiones.coverage')}
          value={t('expedientes.electronico.versiones.coverageValue', {
            items: version.itemCount,
            from: version.folioFrom,
            to: version.folioTo,
          })}
        />
        <Field
          label={t('expedientes.electronico.versiones.algorithm')}
          value={version.signatureAlgorithm}
          mono
        />
        <Field
          label={t('expedientes.electronico.versiones.keyRef')}
          value={
            <Flex alignItems="center" gap={1.5}>
              <LuKeyRound size={12} />
              <Text as="span" fontFamily="monospace" fontSize="sm">{version.signingKeyRef}</Text>
            </Flex>
          }
        />
        <Field
          label={t('expedientes.electronico.versiones.digest')}
          value={version.indexDigest}
          mono
        />
      </Grid>

      {!version.hasXml && (
        <Box
          p={3}
          mb={4}
          borderRadius="lg"
          bg={themeColors.bgCard}
          borderLeft="3px solid"
          borderColor={themeColors.borderHover}
        >
          <Text fontSize="xs" color={themeColors.textSecondary}>
            {t('expedientes.electronico.versiones.noXml')}
          </Text>
        </Box>
      )}

      <Flex gap={2} flexWrap="wrap">
        <ActionButton
          icon={verifying ? <Spinner size="xs" /> : <LuShieldCheck size={14} />}
          onClick={handleVerify}
          disabled={verifying}
        >
          {verifying
            ? t('expedientes.electronico.versiones.verifying')
            : t('expedientes.electronico.versiones.verify')}
        </ActionButton>
        {version.hasXml && (
          <ActionButton icon={<LuFileCode2 size={14} />} onClick={() => handleDownload('xml')}>
            {t('expedientes.electronico.versiones.downloadXml')}
          </ActionButton>
        )}
        <ActionButton icon={<LuPrinter size={14} />} onClick={() => handleDownload('pdf')}>
          {t('expedientes.electronico.versiones.downloadPdf')}
        </ActionButton>
      </Flex>

      <AnimatePresence initial={false}>
        {verificacion && (
          <MotionBox
            key="verif"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            overflow="hidden"
          >
            <Box mt={4}>
              <VerificacionPanel verificacion={verificacion} />
            </Box>
          </MotionBox>
        )}
      </AnimatePresence>
    </MotionBox>
  )
}

function ActionButton({
  icon,
  children,
  onClick,
  disabled,
}: {
  icon: React.ReactNode
  children: React.ReactNode
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <Box
      as="button"
      type="button"
      display="inline-flex"
      alignItems="center"
      gap={2}
      px={3.5}
      py={2}
      borderRadius="lg"
      border="1px solid"
      borderColor={themeColors.border}
      bg="transparent"
      color={themeColors.textSecondary}
      fontSize="xs"
      fontWeight="700"
      cursor={disabled ? 'not-allowed' : 'pointer'}
      opacity={disabled ? 0.6 : 1}
      _hover={disabled ? {} : { borderColor: brand.green, color: brand.green, bg: `${brand.green}08` }}
      disabled={disabled}
      onClick={onClick}
    >
      {icon}
      {children}
    </Box>
  )
}
